import React from "react";

function statusTitle(sentCount, failedCount) {
  if (failedCount === 0) return "Email Send Status: Success";
  if (sentCount === 0) return "Email Send Status: Failed";
  return "Email Send Status: Partial Success";
}

function headerTone(sentCount, failedCount) {
  if (failedCount === 0) return "bg-emerald-50 border-emerald-100";
  if (sentCount === 0) return "bg-red-50 border-red-100";
  return "bg-amber-50 border-amber-100";
}

function statusMessage(sentCount, failedCount, total) {
  const label = total === 1 ? "invoice email" : "invoice emails";
  if (failedCount === 0) {
    return total === 1
      ? "The invoice email was sent successfully."
      : `All ${total} invoice emails were sent successfully.`;
  }
  if (sentCount === 0) {
    return total === 1
      ? "The invoice email could not be sent."
      : `None of the ${total} selected invoice emails could be sent.`;
  }
  return `${sentCount} of ${total} ${label} were sent. ${failedCount} could not be sent — download the list below for details.`;
}

export function downloadFailedInvoiceEmailCsv(rows) {
  if (!rows?.length) return;
  const headers = ["Provider", "Billing Account", "Invoice Date", "Reason"];
  const csvRows = rows.map((r) => [
    r.provider ?? "",
    `="${r.billing_account ?? ""}"`,
    r.invoice_date ?? "",
    r.reason ?? "",
  ]);
  const csv = [headers, ...csvRows]
    .map((row) =>
      row.map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-email-failures-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function missingGlMessage(count, total) {
  const invoiceLabel = count === 1 ? "invoice has" : "invoices have";
  return `${count} selected ${invoiceLabel} no GL code. The email will still be sent, but Accounting may need the GL code added manually.`;
}

const InvoiceEmailSendStatusModal = ({ status, onClose, onConfirmMissingGl }) => {
  if (!status?.open) return null;

  const {
    phase,
    total = 0,
    processed = 0,
    sentCount = 0,
    failed = [],
    missingGl = [],
  } = status;
  const failedCount = failed.length;
  const isSending = phase === "sending";
  const isMissingGl = phase === "missing_gl";
  const missingGlCount = missingGl.length;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const sentPct = total > 0 ? (sentCount / total) * 100 : 0;
  const failedPct = total > 0 ? (failedCount / total) * 100 : 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      role="presentation"
      onClick={isSending ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-email-send-status-title"
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] shadow-xl border border-gray-200 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`px-5 py-4 border-b flex-shrink-0 ${
            isMissingGl
              ? "bg-amber-50 border-amber-100"
              : isSending
                ? "bg-[var(--accent,#a51c1c)]/5 border-[var(--accent,#a51c1c)]/15"
                : headerTone(sentCount, failedCount)
          }`}
        >
          <h2
            id="invoice-email-send-status-title"
            className="text-lg font-semibold text-gray-900 flex items-center gap-2"
          >
            <span aria-hidden="true">{isMissingGl ? "⚠️" : "📧"}</span>
            {isMissingGl
              ? "Missing GL code"
              : isSending
                ? "Sending invoice emails…"
                : statusTitle(sentCount, failedCount)}
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            {isMissingGl
              ? missingGlMessage(missingGlCount, total)
              : isSending
                ? `Sending ${processed} of ${total}… Please keep this window open.`
                : statusMessage(sentCount, failedCount, total)}
          </p>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
          {isMissingGl ? (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      Provider
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      Billing account
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      Invoice date
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      GL code
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {missingGl.map((row, idx) => (
                    <tr key={`${row.billing_account}-${row.invoice_date}-${idx}`} className="bg-amber-50/40">
                      <td className="px-3 py-2 text-gray-900">{row.provider}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-900">
                        {row.billing_account}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                        {row.invoice_date}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-600">
                          No GL
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
          <div>
            <div className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
              <span>Sending progress</span>
              <span>
                {isSending ? `${processed} / ${total}` : `${sentCount} sent · ${failedCount} failed`}
              </span>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden flex">
              {isSending ? (
                <div
                  className="h-full bg-[var(--accent,#a51c1c)] transition-all duration-300 ease-out"
                  style={{ width: `${pct}%` }}
                />
              ) : (
                <>
                  {sentPct > 0 && (
                    <div
                      className="h-full bg-[var(--accent,#a51c1c)]"
                      style={{ width: `${sentPct}%` }}
                      title={`${sentCount} sent`}
                    />
                  )}
                  {failedPct > 0 && (
                    <div
                      className="h-full bg-red-500"
                      style={{ width: `${failedPct}%` }}
                      title={`${failedCount} failed`}
                    />
                  )}
                </>
              )}
            </div>
            {!isSending && total > 0 && (
              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-[var(--accent,#a51c1c)]" />
                  Sent ({sentCount})
                </span>
                {failedCount > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm bg-red-500" />
                    Failed ({failedCount})
                  </span>
                )}
              </div>
            )}
          </div>
          )}

          {!isMissingGl && !isSending && failedCount > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      Provider
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      Billing account
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      Invoice date
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {failed.map((row, idx) => (
                    <tr key={`${row.billing_account}-${row.invoice_date}-${idx}`} className="bg-red-50/40">
                      <td className="px-3 py-2 text-gray-900">{row.provider}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-900">
                        {row.billing_account}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                        {row.invoice_date}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex flex-wrap justify-end gap-2 flex-shrink-0">
          {isMissingGl ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-gray-800 bg-white border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirmMissingGl}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a]"
              >
                Send anyway
              </button>
            </>
          ) : (
            <>
              {!isSending && failedCount > 0 && (
                <button
                  type="button"
                  onClick={() => downloadFailedInvoiceEmailCsv(failed)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download failed list (CSV)
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={isSending}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? "Sending…" : "Close"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceEmailSendStatusModal;
